/**
 * 인쇄 미리보기 복용약 범위 필터 유틸
 *
 * 설계: docs/ai-discussions/20260904-print-drug-scope-filter.md
 * - "복용중" 판정은 처방일+투약일수 기반 근사 — usePrescriptionTimeline과 동일 경계 규약
 *   (end = 처방일 + 투약일수, exclusive → 마지막 복용일 = 처방일 + 투약일수 - 1)
 * - 판정 불가(처방일·투약일수 미상)는 보수적으로 포함 (누락보다 과잉이 안전)
 * - 필터 단위는 **처방전(그룹)**: 복용중 약이 1개라도 있는 처방전은 통째로 표시한다 —
 *   행 단위로 자르면 처방전이 부분 표시돼 실물과 어긋난다 (지니님 확정, 2026-09-05)
 * - "보고서 언급"은 ⑆img 마커·편집본 data-itemcode·S3 URL의 itemCode만 사용 (이름 매칭은 2차).
 *   언급 약은 본표에 끼워 넣지 않고 **별도 참고 표**(약당 최신 처방 1건)로 표시한다
 *
 * parseDate/addDays/startOfDay는 usePrescriptionTimeline의 비공개 헬퍼를 참고한 의도적 중복이다
 * (훅 무수정 유지가 회귀 위험이 낮음). 단, 이쪽은 시작일에 startOfDay 정규화를 추가로 적용하므로
 * 처방일에 시각이 섞인 입력에서는 마지막 날 판정이 훅과 다를 수 있다 (NHIS 처방일은 자정이라
 * 실데이터 영향 없음). end exclusive 규약 자체는 테스트로 고정한다.
 */
import { Prescription, PrescriptionGroup } from '../common/models/types';
import { DRUG_IMAGE_PATTERN } from './drugImageUtils';

/** 처방일 등 날짜 입력 파싱 — 실패 시 null (지면에 "Invalid Date"가 새는 것을 막는 공용 가드) */
export function parseDate(input?: Date | string): Date | null {
  if (!input) return null;
  const d = typeof input === 'string' ? new Date(input) : input;
  return isNaN(d.getTime()) ? null : d;
}

function addDays(date: Date, days: number): Date {
  const d = new Date(date.getTime());
  d.setDate(d.getDate() + days);
  return d;
}

function startOfDay(date: Date): Date {
  const d = new Date(date.getTime());
  d.setHours(0, 0, 0, 0);
  return d;
}

/**
 * 오늘 기준 복용기간 내(= 아직 종료되지 않은) 처방인지 판정한다.
 * 처방일·투약일수 미상은 판정 불가 → 보수적으로 포함(true).
 */
export function isOngoingOn(rx: Prescription, today: Date): boolean {
  const start = parseDate(rx.prescriptionDate);
  if (!start) return true; // 처방일 미상 → 포함

  const days =
    typeof rx.daysOfAdministration === 'number' && rx.daysOfAdministration > 0
      ? rx.daysOfAdministration
      : null;
  if (days === null) return true; // 투약일수 미상 → 포함

  const end = addDays(startOfDay(start), days); // exclusive
  return startOfDay(today) < end;
}

/** 편집본 HTML의 <img data-itemcode="..."> 속성 */
const DATA_ITEMCODE_PATTERN = /data-itemcode="([A-Za-z0-9]+)"/g;
/** 편집본 HTML의 S3 약품 이미지 URL (HtmlViewer의 인식 규칙과 동일 경로) */
const S3_PICTURE_PATTERN = /phai\/drugs\/pictures\/([A-Za-z0-9]+)\.jpg/g;

/**
 * 보고서 콘텐츠(원본 마크다운 또는 편집본 HTML)에서 언급된 약의 itemCode 집합을 추출한다.
 * 전역 정규식의 lastIndex 잔존 함정을 피해 matchAll을 사용한다.
 */
export function extractMentionedItemCodes(content: string): Set<string> {
  const codes = new Set<string>();
  if (!content) return codes;

  const patterns = [DRUG_IMAGE_PATTERN, DATA_ITEMCODE_PATTERN, S3_PICTURE_PATTERN];
  patterns.forEach((pattern) => {
    // 프로젝트 target(es5)에서 이터레이터 직접 순회가 불가해 Array.from으로 변환
    Array.from(content.matchAll(pattern)).forEach((match) => {
      if (match[1]) codes.add(match[1]);
    });
  });
  return codes;
}

/** 처방전 안에 오늘 기준 복용중인 약이 1개라도 있는지 */
export function groupHasOngoing(group: PrescriptionGroup, today: Date): boolean {
  return (group.prescriptions ?? []).some((rx) => isOngoingOn(rx, today));
}

/** "보고서 언급으로 추가될 약" — 표시되는 처방전에 없으면서 보고서에 언급된 품목의 최신 처방 1건 */
export interface MentionedAddition {
  itemCode: string;
  medicationName: string;
  /** 이 품목의 가장 최근 처방 행 — 참고 표의 처방일·병원·용량용법 표기에 사용 */
  latestPrescription: Prescription;
}

/**
 * "보고서 언급으로 추가될 약" 목록을 계산한다 — 별도 참고 표와 패널 "N종 추가" 라벨·툴팁용.
 * - 복용중 처방전(본표에 표시될 그룹)에 이미 등장하는 품목은 제외 (중복 인쇄 방지, 지니님 확정)
 * - 품목당 **최신 처방 1건**만 담는다 — 반복처방 이력을 다 끌고 오면 표가 폭발한다
 * - 최신 처방일 내림차순 정렬
 */
export function getMentionedAdditions(
  groups: PrescriptionGroup[],
  mentionedItemCodes: Set<string>,
  today: Date
): MentionedAddition[] {
  // 본표(복용중 처방전 단위)에 이미 표시될 품목 집합 — 처방전 통째 표시라 종료약도 포함된다
  const shownCodes = new Set<string>();
  groups.forEach((g) => {
    if (!groupHasOngoing(g, today)) return;
    (g.prescriptions ?? []).forEach((rx) => {
      if (rx.itemCode) shownCodes.add(rx.itemCode);
    });
  });

  // 품목별 최신 처방 1건 선택
  const latestByCode = new Map<string, Prescription>();
  groups.forEach((g) => {
    (g.prescriptions ?? []).forEach((rx) => {
      if (!rx.itemCode || !mentionedItemCodes.has(rx.itemCode)) return;
      if (shownCodes.has(rx.itemCode)) return; // 본표에 이미 있으면 참고 표 대상 아님
      const prev = latestByCode.get(rx.itemCode);
      const prevDate = prev ? parseDate(prev.prescriptionDate)?.getTime() ?? 0 : -1;
      const curDate = parseDate(rx.prescriptionDate)?.getTime() ?? 0;
      if (!prev || curDate > prevDate) latestByCode.set(rx.itemCode, rx);
    });
  });

  return Array.from(latestByCode.entries())
    .map(([itemCode, rx]) => ({
      itemCode,
      medicationName: rx.medicationName || itemCode,
      latestPrescription: rx,
    }))
    .sort((a, b) => {
      const ta = parseDate(a.latestPrescription.prescriptionDate)?.getTime() ?? 0;
      const tb = parseDate(b.latestPrescription.prescriptionDate)?.getTime() ?? 0;
      return tb - ta;
    });
}

/**
 * 참고 섹션의 "마지막 처방전 통째" 모드용 — 언급 약(getMentionedAdditions 결과)의 최신 처방이
 * 속한 처방전을 **통째로** 반환한다. 약사는 병용약 맥락으로 그 약의 용도를 추정하므로,
 * 약 단독 행보다 처방전 전체가 필요한 경우가 있다 (지니님 확정, 2026-09-05).
 * - 여러 언급 약이 같은 처방전에 속하면 그 처방전은 1번만 (dedupe)
 * - 행 참조 동일성으로 매칭 — prescriptionGroupId 유무에 의존하지 않는다
 */
export function getMentionedAdditionGroups(
  groups: PrescriptionGroup[],
  additions: MentionedAddition[]
): PrescriptionGroup[] {
  if (additions.length === 0) return [];
  const wantedRows = new Set(additions.map((a) => a.latestPrescription));
  return groups.filter((g) => (g.prescriptions ?? []).some((rx) => wantedRows.has(rx)));
}

/** 옵션별 "켜면 지면이 실제로 바뀌는가" 판정 — 패널의 무효 옵션 비활성+사유 표시에 쓴다 */
export interface PrintOptionAvailability {
  /** 종료 예상 배지가 붙을 수 있는(오늘 기준 종료된) 약이 1건이라도 있는가 */
  hasEndedDrugs: boolean;
  /** 약효분류번호(meftDivNo)를 가진 처방 행이 있는가 */
  hasMeftCodes: boolean;
  /** ATC 코드를 가진 처방 행이 있는가 */
  hasAtcCodes: boolean;
  /** 본문 의약품 카드(⑆img/편집본 itemCode)가 보고서에 있는가 */
  hasDrugCards: boolean;
}

/**
 * 옵션 가용성을 계산한다 — 약사 인쇄 화면과 탑재뷰가 같은 판정을 공유한다.
 * ("옵션을 눌렀는데 아무 변화가 없어 가만있어 보이는" 혼란 방지, 지니님 확정 2026-09-05)
 */
export function computeOptionAvailability(
  groups: PrescriptionGroup[],
  mentionedItemCodes: Set<string>,
  today: Date
): PrintOptionAvailability {
  const rxs = groups.reduce<Prescription[]>(
    (acc, g) => acc.concat(g.prescriptions ?? []),
    []
  );
  return {
    hasEndedDrugs: rxs.some((rx) => !isOngoingOn(rx, today)),
    hasMeftCodes: rxs.some((rx) => !!rx.meftDivNo),
    hasAtcCodes: rxs.some((rx) => !!rx.atcCode),
    hasDrugCards: mentionedItemCodes.size > 0,
  };
}

export interface DrugScopeFilterResult {
  groups: PrescriptionGroup[];
  /** 필터 전 처방전(그룹) 수 */
  totalGroups: number;
  /** 필터 후 처방전(그룹) 수 */
  shownGroups: number;
}

/**
 * 복용약 범위를 처방전 그룹에 적용한다 — **처방전 단위** 필터.
 * 'ongoing'이면 복용중 약이 1개라도 있는 처방전만 남기고(내부 행은 자르지 않음),
 * 범위 주석용 그룹 수 통계를 함께 반환한다.
 */
export function filterPrescriptionGroupsForPrint(
  groups: PrescriptionGroup[],
  scope: 'all' | 'ongoing',
  today: Date
): DrugScopeFilterResult {
  const filteredGroups =
    scope === 'all' ? groups : groups.filter((g) => groupHasOngoing(g, today));

  return { groups: filteredGroups, totalGroups: groups.length, shownGroups: filteredGroups.length };
}
